import { CARD_TARGET } from "@/src/hooks/features/posts/use-sticker-session";
import React, { useMemo, useRef } from "react";
import { Image, Platform, Pressable, Text, View } from "react-native";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import { useTranslation } from "react-i18next";
import { STICKER_BY_ID } from "@/src/constants/stickers";
import type { StickerPlacement } from "@/src/services/api/posts/engagement-functions";
import type { StickerSession } from "@/src/hooks/features/posts/use-sticker-session";
import { clamp, ROT, round, SCALE, stickerPx, StickerDraft } from "./sticker-geometry";
import PlacedSticker from "@/src/features/sticker-display/placed-sticker";
import { stickerAddedBy } from "@/src/features/sticker-display/sticker-tooltip";

type Box = { left: number; top: number; w: number; h: number };
type Props = Readonly<{ session: StickerSession; box: Box; stickers: StickerPlacement[] }>;

const webOnly = (s: object) => (Platform.OS === "web" ? s : {});

const StickerImage = ({ s, box }: { s: StickerDraft; box: Box }) => {
  const def = STICKER_BY_ID[s.stickerId];
  const px = stickerPx(box, s.scale);
  return def ? (
    <Image source={def.source} style={{ width: px, height: px }} accessibilityLabel={def.label} {...({ draggable: false } as any)} />
  ) : null;
};

const place = (s: StickerDraft, box: Box) => {
  const px = stickerPx(box, s.scale);
  return {
    position: "absolute" as const,
    left: s.x * box.w - px / 2,
    top: s.y * box.h - px / 2,
    width: px,
    height: px,
    transform: [{ rotate: `${s.rotation}deg` }],
  };
};

/**
 * Everyone's stickers drawn over the post's own media (or text block), at
 * fractional positions of the rendered box. The viewer's sticker is editable
 * in place: drag, pinch to resize, two-finger rotate. Only the sticker itself
 * captures gestures, so the feed keeps scrolling everywhere else.
 */
const StickerOverlay = ({ session, box, stickers }: Props) => {
  const { t } = useTranslation();
  const { draft, setDraft, userId, focused, setFocused, mediaIndex, canModerate, remove, pending } = session;
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const start = useRef<StickerDraft | null>(null);
  const boxRef = useRef(box);
  boxRef.current = box;

  const gesture = useMemo(() => {
    const begin = () => { start.current = draftRef.current; };
    const pan = Gesture.Pan()
      .runOnJS(true)
      .minDistance(1)
      .onStart(begin)
      .onUpdate((e) => {
        const s = start.current;
        if (!s) return;
        setDraft((d) => d && {
          ...d,
          x: round(clamp(s.x + e.translationX / boxRef.current.w, 0, 1)),
          y: round(clamp(s.y + e.translationY / boxRef.current.h, 0, 1)),
        });
      });
    const pinch = Gesture.Pinch()
      .runOnJS(true)
      .onStart(begin)
      .onUpdate((e) => {
        const s = start.current;
        if (s) setDraft((d) => d && { ...d, scale: round(clamp(s.scale * e.scale, SCALE.min, SCALE.max), 2) });
      });
    const rotation = Gesture.Rotation()
      .runOnJS(true)
      .onStart(begin)
      .onUpdate((e) => {
        const s = start.current;
        if (s) setDraft((d) => d && { ...d, rotation: Math.round(clamp(s.rotation + (e.rotation * 180) / Math.PI, ROT.min, ROT.max)) });
      });
    return Gesture.Simultaneous(pan, pinch, rotation);
  }, [setDraft]);

  const others = stickers.filter((s) => (mediaIndex === CARD_TARGET || s.mediaIndex === mediaIndex) && !(draft && s.userId === userId));
  const focusedSticker = focused ? others.find((s) => s.userId === focused) : undefined;

  return (
    <View
      pointerEvents="box-none"
      testID="sticker-overlay"
      style={{ position: "absolute", left: box.left, top: box.top, width: box.w, height: box.h }}
    >
      {others.map((s) => (
        <PlacedSticker
          key={s.userId}
          s={s}
          box={box}
          testID={`sticker-placed-${s.userId === userId ? "mine" : s.stickerId}`}
          label={stickerAddedBy(s, userId, t)}
          onPress={() => setFocused(focused === s.userId ? null : s.userId)}
          opacity={pending && s.userId === userId ? 0.6 : 1}
        />
      ))}
      {draft ? (
        <GestureDetector gesture={gesture}>
          <View
            testID="sticker-draft"
            accessibilityLabel={t("LABEL_STICKER_DRAG_HINT")}
            style={[place(draft, box), webOnly({ touchAction: "none", cursor: "grab", userSelect: "none" }) as any]}
          >
            <View
              pointerEvents="none"
              style={{ position: "absolute", inset: -4, borderWidth: 1.5, borderStyle: "dashed", borderColor: "#fff", borderRadius: 8, opacity: 0.9 } as any}
            />
            <StickerImage s={draft} box={box} />
          </View>
        </GestureDetector>
      ) : null}
      {focusedSticker ? (
        <View
          testID="sticker-who"
          className="absolute flex-row items-center gap-2 bg-chalk rounded-full px-3 py-1 shadow"
          style={{ left: 8, bottom: 8 }}
        >
          {focusedSticker.profileImageUrl ? (
            <Image source={{ uri: focusedSticker.profileImageUrl }} style={{ width: 20, height: 20, borderRadius: 10 }} />
          ) : null}
          <Text className="text-xs text-onyx">
            {focusedSticker.userId === userId ? t("LABEL_YOU") : focusedSticker.displayName || t("LABEL_STICKER")}
          </Text>
          {focusedSticker.userId === userId || canModerate ? (
            <Pressable
              testID="sticker-remove-focused"
              onPress={() => { setFocused(null); remove(focusedSticker.userId === userId ? undefined : focusedSticker.userId); }}
            >
              <Text className="text-xs text-danger font-semibold">{t("LABEL_REMOVE")}</Text>
            </Pressable>
          ) : null}
        </View>
      ) : null}
    </View>
  );
};

export default StickerOverlay;
